import { useCallback } from 'react'
import { useNavigate, useParams } from 'react-router'

import { documentRoute, routes } from '@/app/routes'
import { useResolveTask } from '@/features/tasks/mutations'
import { useDocuments, useRoadmap, useTasks } from '@/shared/api/queries'
import type { Task } from '@/shared/api/queries'
import { formatFullDate, useDeadlineText } from '@/shared/format'
import { useLocalization, useTranslate } from '@/shared/i18n'
import type { TranslationKey } from '@/shared/i18n'
import {
  areaIcon,
  areaLabel,
  documentIcon,
  documentTypeLabel,
  evaluationsOf,
  requiredDocumentsOf,
  requirementOf,
  urgencyOf,
} from '@/shared/model'
import type { RequiredDocument } from '@/shared/model'
import {
  Alert,
  Badge,
  Button,
  Card,
  KeyValue,
  Progress,
  Row,
  Skeleton,
  Stack,
  StateBlock,
  Text,
  useToast,
} from '@/shared/ui'
import { useMainButton } from '@/shared/telegram'

function priorityLabel(priority: string): TranslationKey {
  return `priority.${priority}` as TranslationKey
}

function ChecklistLine({ required }: { required: RequiredDocument }) {
  const t = useTranslate()
  const label = t(documentTypeLabel(required.type))
  const stored = required.document

  return (
    <Row
      variant="plain"
      to={stored ? documentRoute(stored.id) : routes.documents}
      title={label}
      meta={
        stored
          ? t(`documents.status.${stored.status}` as TranslationKey)
          : t('task.document.missing')
      }
      trailing={
        <span className={stored ? 'check check--done' : 'check'} aria-hidden="true">
          {stored ? '✓' : ''}
        </span>
      }
    />
  )
}

function Details({ task }: { task: Task }) {
  const t = useTranslate()
  const { language } = useLocalization()
  const navigate = useNavigate()
  const deadline = useDeadlineText()

  const roadmap = useRoadmap()
  const documents = useDocuments()
  const resolve = useResolveTask()
  const toast = useToast()

  const evaluations = evaluationsOf(roadmap.data)
  const requirement = requirementOf(task, evaluations)
  const area = requirement?.category ?? 'other'
  const required = requiredDocumentsOf(requirement, documents.data ?? [])
  const stored = required.filter((entry) => entry.document)

  const urgency = task.status === 'open' ? urgencyOf(task) : 'done'
  const open = task.status === 'open'

  const complete = useCallback(
    () =>
      resolve.mutate(
        { id: task.id, outcome: 'done' },
        { onSuccess: () => toast(t('toast.task.done'), 'success') },
      ),
    [resolve, task.id, toast, t],
  )

  useMainButton(
    open ? { text: t('tasks.complete'), onClick: complete, loading: resolve.isPending } : undefined,
  )

  return (
    <Stack gap="md" className="page--detail">
      <Card className={`tone--${urgency}`}>
        <Stack direction="row" gap="sm" wrap>
          <Badge tone>{t(priorityLabel(task.priority))}</Badge>
          <Text as="span" role="label" tone="muted">
            {areaIcon(area)} {t(areaLabel(area))}
          </Text>
        </Stack>

        <Text as="h1" role="heading-2">
          {task.title}
        </Text>

        <Stack gap="xs">
          <Text role="eyebrow" tone="muted">
            {t('task.deadline')}
          </Text>
          <Stack direction="row" align="start" gap="sm" wrap>
            <Text as="span" role="date" tone="tone">
              {task.deadline ? formatFullDate(task.deadline, language) : t('task.deadline.none')}
            </Text>
            {task.deadline ? (
              <Text as="span" role="label" tone="muted">
                {deadline(task.deadline)}
              </Text>
            ) : null}
          </Stack>
        </Stack>

        {task.description ? (
          <Text role="body" tone="muted" measure>
            {task.description}
          </Text>
        ) : null}
      </Card>

      {required.length > 0 ? (
        <Card>
          <Stack direction="row" justify="between" gap="sm">
            <Text as="h2" role="eyebrow" tone="muted">
              {t('task.checklist')}
            </Text>
            <Text as="span" role="label" tone="muted">
              {t('task.checklist.progress', { done: stored.length, total: required.length })}
            </Text>
          </Stack>
          <Progress value={stored.length} max={required.length} label={t('task.documents')} />
          <Stack as="ul" gap="xs">
            {required.map((entry) => (
              <ChecklistLine key={entry.type} required={entry} />
            ))}
          </Stack>
        </Card>
      ) : null}

      {stored.length > 0 ? (
        <Card>
          <Text as="h2" role="eyebrow" tone="muted">
            {t('task.connected')}
          </Text>
          <Stack as="ul" gap="xs">
            {stored.map((entry) => (
              <Row
                key={entry.type}
                variant="card"
                to={documentRoute(entry.document?.id ?? '')}
                icon={documentIcon(entry.type)}
                title={t(documentTypeLabel(entry.type))}
                meta={
                  entry.document?.expires_on
                    ? t('documents.expires', {
                        date: formatFullDate(entry.document.expires_on, language),
                      })
                    : t('documents.no_dates')
                }
              />
            ))}
          </Stack>
        </Card>
      ) : null}

      {task.source ? (
        <Card flat>
          <KeyValue label={t('task.source')}>{task.source}</KeyValue>
        </Card>
      ) : null}

      {open ? (
        <Stack direction="row" gap="sm">
          <Button
            variant="quiet"
            disabled={resolve.isPending}
            onClick={() =>
              resolve.mutate(
                { id: task.id, outcome: 'dismissed' },
                {
                  onSuccess: () => {
                    toast(t('toast.task.dismissed'))
                    void navigate(routes.tasks)
                  },
                },
              )
            }
          >
            {t('tasks.dismiss')}
          </Button>
        </Stack>
      ) : (
        <Text role="caption" tone="muted">
          {task.status === 'done' ? t('tasks.status.done') : t('tasks.status.dismissed')}
        </Text>
      )}
    </Stack>
  )
}

export function TaskPage() {
  const t = useTranslate()
  const { id } = useParams()
  const { data, error, isPending, refetch } = useTasks()

  const task = data?.find((candidate) => candidate.id === id)

  return (
    <StateBlock
      pending={isPending}
      error={error}
      onRetry={() => void refetch()}
      skeleton={<Skeleton variant="card" rows={3} title />}
    >
      {task ? <Details task={task} /> : <Alert status="danger" title={t('error.generic.title')} />}
    </StateBlock>
  )
}
